import { Component, ReactNode, useState } from "react";
import { ringLogger } from "@/lib/ringBufferLogger";
import { DiagnosticReportModal } from "@/components/support/DiagnosticReportModal";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

function ErrorFallback({ error, onReload }: { error: Error | null; onReload: () => void }) {
  const [reportModalOpen, setReportModalOpen] = useState(false);

  return (
    <>
      <div className="h-screen overflow-hidden bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <h1 className="text-2xl font-bold text-foreground font-serif">
            Something went wrong
          </h1>
          <p className="text-foreground text-sm">
            An unexpected error occurred. If you are on the Birch Android app, your manuscripts are stored locally and remain safe.
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={onReload}
              className="w-full min-h-[48px] text-base font-bold border-2 border-foreground bg-foreground text-background hover:bg-background hover:text-foreground"
            >
              Reload App
            </button>
            <button
              onClick={() => setReportModalOpen(true)}
              className="w-full min-h-[48px] text-base font-bold border-2 border-foreground bg-background text-foreground hover:bg-accent"
            >
              Send Diagnostic Report
            </button>
          </div>

          {error && (
            <details className="text-left mt-4">
              <summary className="cursor-pointer text-xs text-muted-foreground font-mono">
                Error details
              </summary>
              <pre className="mt-2 p-3 bg-muted text-[11px] overflow-auto border border-foreground max-h-40 font-mono">
                {error.message}
                {error.stack && `\n${error.stack}`}
              </pre>
            </details>
          )}
        </div>
      </div>

      <DiagnosticReportModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        initialCategory="Bug / Crash Report"
        initialMessage={error ? `App crashed with error: ${error.message}` : "App crashed unexpectedly"}
      />
    </>
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    ringLogger.error(`[ErrorBoundary] ${error.message}`, {
      stack: error.stack?.slice(0, 500),
      componentStack: errorInfo.componentStack?.slice(0, 500),
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onReload={this.handleReload} />;
    }

    return this.props.children;
  }
}
